import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, formatDate, formatIDR, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, MapPin, CheckCircle2, XCircle, Tag, Clock, AlertCircle } from "lucide-react";

/**
 * RequestDetail — two-way negotiation flow.
 *
 *   pending_price → Jastiper sets a quote
 *   priced        → Buyer accepts (creates Escrow order) or declines
 *   agreed        → an order_id is linked; show "Lihat Order"
 *   declined      → terminal
 */
const STATUS_BADGE = {
  pending_price: { bg: "#FEF3C7", text: "#92400E", label: "Menunggu Harga" },
  priced: { bg: "#E0E7FF", text: "#3730A3", label: "Sudah Diberi Harga" },
  agreed: { bg: "#DCFCE7", text: "#166534", label: "Disepakati" },
  declined: { bg: "#FEE2E2", text: "#991B1B", label: "Ditolak" },
};

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [quoted, setQuoted] = useState("");
  const [note, setNote] = useState("");

  const reload = () => api.get(`/requests/${id}`).then((r) => setReq(r.data));

  useEffect(() => {
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Memuat...</div>;
  if (!req) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Permintaan tidak ditemukan</div>;

  const isBuyer = user?.id === req.buyer_id;
  const isJastiper = user?.id === req.target_jastiper_id;
  const statusInfo = STATUS_BADGE[req.status] || STATUS_BADGE.pending_price;

  const setPrice = async () => {
    const v = parseFloat(quoted);
    if (!v || v <= 0) {
      toast.error("Masukkan harga yang valid");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/requests/${id}/set-price`, { quoted_price: v, jastiper_note: note });
      toast.success("Harga terkirim ke Buyer.");
      setQuoted("");
      setNote("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal mengirim harga");
    } finally {
      setBusy(false);
    }
  };

  const acceptPrice = async () => {
    if (!window.confirm(`Setujui harga ${formatIDR(req.quoted_price)}? Order Escrow akan dibuat.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/requests/${id}/accept-price`);
      toast.success("Disetujui! Lanjut ke pembayaran.");
      navigate(`/orders/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menyetujui");
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Yakin ingin menolak permintaan ini?")) return;
    setBusy(true);
    try {
      await api.post(`/requests/${id}/decline`);
      toast.success("Permintaan ditolak.");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menolak");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="request-detail-page">
      <Link to="/requests" className="text-sm flex items-center gap-2" style={{color: "var(--app-muted)"}} data-testid="back-to-requests">
        <ArrowLeft size={14} strokeWidth={1.5} /> Semua Permintaan
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="label-caps mb-2 flex items-center gap-2">
              <MapPin size={12} strokeWidth={1.5} /> Dari {req.source_country}
            </div>
            <h1 className="font-display text-4xl tracking-tight" data-testid="request-item-name">{req.item_name}</h1>
            <div className="font-mono text-sm mt-2 flex items-center gap-3" style={{color: "var(--app-muted)"}}>
              <span>Diposting {formatDate(req.created_at)}</span>
              {req.deadline && <><span>·</span><span>Tenggat {formatDate(req.deadline)}</span></>}
            </div>
            <div className="mt-3">
              <span
                className="label-caps px-3 py-1.5 font-mono text-xs"
                style={{ backgroundColor: statusInfo.bg, color: statusInfo.text }}
                data-testid="request-status-badge"
              >
                {statusInfo.label}
              </span>
            </div>
          </div>

          {req.photo_path && (
            <div className="border" style={{borderColor: "var(--app-border)"}}>
              <img src={fileUrl(req.photo_path)} alt={req.item_name} className="w-full max-h-96 object-contain bg-[var(--app-surface-alt)]" />
            </div>
          )}

          <div className="surface p-6">
            <div className="label-caps mb-3">Deskripsi</div>
            <p className="leading-relaxed whitespace-pre-wrap">{req.description}</p>
          </div>

          {req.jastiper_note && (
            <div className="surface p-6" data-testid="request-jastiper-note">
              <div className="label-caps mb-3">Catatan dari Jastiper</div>
              <p className="leading-relaxed whitespace-pre-wrap">{req.jastiper_note}</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Counterparty card */}
          <div className="surface p-5">
            <div className="label-caps mb-3">{isBuyer ? "Jastiper Target" : "Pembeli"}</div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center font-display font-medium text-white" style={{backgroundColor: "var(--app-brand)"}}>
                {(isBuyer ? req.target_jastiper_name : req.buyer_name)?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-medium">{isBuyer ? req.target_jastiper_name : req.buyer_name}</div>
              </div>
            </div>
          </div>

          {/* Pricing card */}
          {req.quoted_price ? (
            <div className="surface p-5" data-testid="quoted-price-card">
              <div className="label-caps mb-3 flex items-center gap-2">
                <Tag size={12} strokeWidth={1.5} /> Penawaran Jastiper
              </div>
              <div className="font-display text-3xl tracking-tight" style={{color: "var(--app-brand)"}} data-testid="quoted-price-amount">
                {formatIDR(req.quoted_price)}
              </div>
              <div className="text-xs mt-2" style={{color: "var(--app-muted)"}}>
                Sudah termasuk biaya jasa Jastiper. Platform fee akan ditambahkan di checkout.
              </div>
            </div>
          ) : (
            <div className="surface p-5" data-testid="awaiting-quote-card">
              <div className="label-caps mb-3 flex items-center gap-2">
                <Clock size={12} strokeWidth={1.5} /> Menunggu
              </div>
              <p className="text-sm" style={{color: "var(--app-muted)"}}>
                Jastiper belum memberi penawaran harga.
              </p>
            </div>
          )}

          {/* Jastiper: Set Price form */}
          {isJastiper && (req.status === "pending_price" || req.status === "priced") && (
            <div className="surface p-5 space-y-3" data-testid="jastiper-set-price-form">
              <div className="label-caps">{req.status === "priced" ? "Perbarui Harga" : "Beri Harga"}</div>
              <input
                type="number"
                className="input-flat font-mono text-lg"
                value={quoted}
                onChange={(e) => setQuoted(e.target.value)}
                placeholder="Harga total (IDR)"
                data-testid="set-price-input"
              />
              <textarea
                className="input-flat text-sm min-h-[80px]"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Catatan untuk pembeli (opsional)"
                data-testid="set-price-note"
              />
              <button
                onClick={setPrice}
                disabled={busy}
                className="btn-primary-flat w-full"
                data-testid="submit-price-btn"
              >
                {busy ? "Mengirim..." : (req.status === "priced" ? "Perbarui Penawaran" : "Kirim Penawaran")}
              </button>
              {req.status !== "agreed" && (
                <button onClick={decline} disabled={busy} className="btn-secondary-flat w-full text-sm" data-testid="jastiper-decline-btn">
                  <XCircle size={14} strokeWidth={1.5} /> Tolak Permintaan
                </button>
              )}
            </div>
          )}

          {/* Buyer: Accept / Decline */}
          {isBuyer && req.status === "priced" && (
            <div className="space-y-2" data-testid="buyer-action-panel">
              <button
                onClick={acceptPrice}
                disabled={busy}
                className="btn-primary-flat w-full"
                data-testid="accept-price-btn"
              >
                <CheckCircle2 size={16} strokeWidth={1.5} /> Terima & Bayar
              </button>
              <button
                onClick={decline}
                disabled={busy}
                className="btn-secondary-flat w-full text-sm"
                data-testid="buyer-decline-btn"
              >
                <XCircle size={14} strokeWidth={1.5} /> Tolak Penawaran
              </button>
            </div>
          )}

          {/* Agreed → link to order */}
          {req.status === "agreed" && req.order_id && (
            <Link
              to={`/orders/${req.order_id}`}
              className="btn-primary-flat w-full"
              data-testid="view-order-link"
            >
              Lihat Order →
            </Link>
          )}

          {/* Terminal info */}
          {req.status === "declined" && (
            <div className="border p-4 text-center text-sm" style={{borderColor: "var(--app-border)", color: "var(--app-muted)"}} data-testid="declined-message">
              <AlertCircle size={20} strokeWidth={1.5} className="mx-auto mb-2" />
              Permintaan ini sudah ditolak.
            </div>
          )}

          {!user && (
            <Link to="/auth" className="btn-primary-flat w-full" data-testid="signin-to-act">Masuk untuk melanjutkan</Link>
          )}
        </div>
      </div>
    </div>
  );
}
